/**
 * AccessPolicy configuration for quarantine isolation.
 *
 * Resolves the standard/admin AccessPolicies by their stable identifier at
 * runtime, so quarantine isolation has no per-environment config to forget:
 * - Standard users get a policy that excludes quarantined resources.
 * - Vibrant admins get a policy that allows access to quarantined resources.
 *
 * The policies are created (once per project) by
 * scripts/medplum/bootstrap-access-policies.ts. There is intentionally no
 * MEDPLUM_*_ACCESS_POLICY_ID env var — the previous env-var design silently
 * attached no policy when unset, which left quarantine isolation off in prod for
 * months. Here, a missing standard policy in a real environment is fatal.
 *
 * @see scripts/medplum/bootstrap-access-policies.ts for policy creation
 * @see docs/data/access-model.md for the quarantine policy model
 */

import type { MedplumClient } from "@medplum/core";
import type {
  AccessPolicy,
  Patient,
  Practitioner,
  ProjectMembership,
  ProjectMembershipAccess,
  Reference,
} from "@medplum/fhirtypes";
import { Environments, getEnvironment } from "@/lib/environment";
import { paginatedSearch } from "@/lib/fhir/bundle";
import { createLogger } from "@/lib/logger";
import { createMedplumAdminClient } from "@/lib/medplum/admin";

const logger = createLogger("access-policy");

/**
 * Identifier system retained for the gitignored access-policy probes only.
 * @deprecated AccessPolicy is resolved by `name`, not a FHIR identifier — see
 * {@link ACCESS_POLICY_NAMES}.
 */
export const ACCESS_POLICY_IDENTIFIER_SYSTEM = "vibrant.access-policy";

/** Stable logical keys for the quarantine + patient-scope AccessPolicies. */
export const AccessPolicyIdentifiers = {
  /** Policy that excludes quarantined resources - for standard users */
  STANDARD_EXCLUDES_QUARANTINED: "standard-excludes-quarantined",
  /** Policy that includes quarantined resources - for Vibrant admins */
  ADMIN_INCLUDES_QUARANTINED: "admin-includes-quarantined",
  /** Policy that scopes a patient session to their own Patient compartment (ENG-4663) */
  PATIENT_SCOPE_V1: "patient-scope-v1",
} as const;

/**
 * The `name` the bootstrap script assigns each policy, keyed by logical key.
 *
 * Medplum's AccessPolicy has no usable FHIR `identifier` — the server rejects
 * `identifier` on write ("Invalid additional property") and exposes no
 * `identifier` search param — so `name` is the stable lookup key. The bootstrap
 * script and {@link resolveAccessPolicyRef} share this map so they cannot drift.
 */
export const ACCESS_POLICY_NAMES: Record<string, string> = {
  [AccessPolicyIdentifiers.STANDARD_EXCLUDES_QUARANTINED]:
    "Vibrant Standard - Excludes Quarantined",
  [AccessPolicyIdentifiers.ADMIN_INCLUDES_QUARANTINED]: "Vibrant Admin - Can See Quarantined",
  [AccessPolicyIdentifiers.PATIENT_SCOPE_V1]: "Vibrant Patient - Own Chart Scope V1",
};

// Process-lifetime memo. These policies are static config created once per
// project by the bootstrap script; a re-bootstrap (new UUID) is rare and a
// process restart clears this — cheaper than a Medplum round-trip per invite.
const policyRefCache = new Map<string, Reference<AccessPolicy>>();

/** @internal Test-only — clears the resolver memo so tests stay order-independent. */
export function _clearAccessPolicyRefCache(): void {
  policyRefCache.clear();
}

/** True when `policy` is the one the logical key names (matched by `name`). */
function policyMatchesKey(policy: AccessPolicy, policyKey: string): boolean {
  const expectedName = ACCESS_POLICY_NAMES[policyKey];
  return !!expectedName && policy.name === expectedName;
}

/**
 * Resolves an AccessPolicy reference for a logical key (see
 * {@link AccessPolicyIdentifiers}), matching on the policy `name`.
 *
 * AccessPolicy can't be resolved by FHIR `identifier` in Medplum — the server
 * rejects `identifier` on write and exposes no `identifier` search param — and
 * there is no `name` search param either, so we list and match in-memory.
 * Memoized for the process lifetime.
 *
 * Pages through every AccessPolicy rather than reading a single page: each
 * practice can add its own integration policies, so the total can exceed one
 * page, and a target policy beyond the first page would otherwise be invisible —
 * making {@link resolveOrThrowInProd} reject a valid invite as "not found".
 *
 * Returns undefined when no policy matches — callers decide whether that is
 * fatal (see {@link getStandardMembershipAccess}).
 */
export async function resolveAccessPolicyRef(
  medplumAdmin: MedplumClient,
  policyKey: string,
): Promise<Reference<AccessPolicy> | undefined> {
  const cached = policyRefCache.get(policyKey);
  if (cached) {
    return cached;
  }

  // Collect every name match rather than short-circuiting on the first. `name`
  // is the durable lookup key, so two policies sharing it is ambiguous — taking
  // whichever paginates first would let every downstream lookup silently use an
  // arbitrary one. Fail loud with the IDs instead. _count: 1000 keeps this to
  // one page in practice while staying correct if a project ever exceeds it.
  const matchIds: string[] = [];
  for await (const policy of paginatedSearch<AccessPolicy>({
    medplum: medplumAdmin,
    resourceType: "AccessPolicy",
    query: { _count: "1000" },
  })) {
    if (policy.id && policyMatchesKey(policy, policyKey)) matchIds.push(policy.id);
  }

  if (matchIds.length > 1) {
    throw new Error(
      `Ambiguous AccessPolicy "${ACCESS_POLICY_NAMES[policyKey] ?? policyKey}": ` +
        `${matchIds.length} policies share this name (${matchIds.join(", ")}). ` +
        "Delete the duplicates so name-based resolution is deterministic, then re-run.",
    );
  }

  const id = matchIds[0];
  if (!id) return undefined;

  const ref: Reference<AccessPolicy> = { reference: `AccessPolicy/${id}` };
  policyRefCache.set(policyKey, ref);
  return ref;
}

/**
 * Resolves a policy ref, throwing in real environments (prod/sandbox) when it's
 * missing. This is the fail-loud the env-var design lacked: an unbootstrapped
 * deploy refuses to mint an unprotected membership instead of leaking quietly.
 * In local/preview we soft-skip so an un-bootstrapped dev project still works.
 */
async function resolveOrThrowInProd(
  medplumAdmin: MedplumClient,
  policyKey: string,
): Promise<Reference<AccessPolicy> | undefined> {
  const ref = await resolveAccessPolicyRef(medplumAdmin, policyKey);
  if (ref) {
    return ref;
  }

  if (getEnvironment() >= Environments.SANDBOX) {
    throw new Error(
      `AccessPolicy "${ACCESS_POLICY_NAMES[policyKey] ?? policyKey}" not found. ` +
        "Run scripts/medplum/bootstrap-access-policies.ts before inviting users — " +
        "refusing to create a membership without quarantine isolation.",
    );
  }
  logger.warn("AccessPolicy not found; skipping assignment (non-prod env)", {
    policyKey,
    environment: getEnvironment().name,
  });
  return undefined;
}

/**
 * ProjectMembership access for a standard user (providers, staff, patients):
 * the quarantine-excluding policy.
 *
 * Fail-loud: a missing standard policy in prod/sandbox throws, because attaching
 * no policy is exactly the silent leak we are fixing.
 */
export async function getStandardMembershipAccess(
  medplumAdmin: MedplumClient,
): Promise<ProjectMembershipAccess[] | undefined> {
  const policyRef = await resolveOrThrowInProd(
    medplumAdmin,
    AccessPolicyIdentifiers.STANDARD_EXCLUDES_QUARANTINED,
  );
  return policyRef ? [{ policy: policyRef }] : undefined;
}

/** The `ProjectMembership.access[].parameter[].name` bound to the active patient's own reference. */
export const PATIENT_SCOPE_PARAMETER_NAME = "current_patient";

/** The `ProjectMembership.access[].parameter[].name` bound to the active patient's managing organization. */
export const ORGANIZATION_SCOPE_PARAMETER_NAME = "current_organization";

/**
 * ProjectMembership access for a patient profile: the patient-compartment-scoped
 * policy, parameterized to that patient's own reference.
 *
 * Fail-loud: a missing patient-scope policy in prod/sandbox throws, because
 * attaching no policy (or the non-isolating standard policy) to a patient
 * membership is exactly the cross-patient leak this policy exists to close
 * (see ENG-4663).
 */
export async function getPatientMembershipAccess(
  medplumAdmin: MedplumClient,
  patientRef: Reference<Patient>,
  organizationRef: Reference | undefined,
): Promise<ProjectMembershipAccess[] | undefined> {
  const policyRef = await resolveOrThrowInProd(
    medplumAdmin,
    AccessPolicyIdentifiers.PATIENT_SCOPE_V1,
  );

  if (!policyRef) return undefined;

  if (!organizationRef) {
    // Include the patient so this is attributable: it fires from the invite/reinvite
    // paths, and patients with no managingOrganization demonstrably exist (the backfill
    // has a whole status for them), so an operator hitting it needs to know which one.
    throw new Error(
      `getPatientMembershipAccess missing organizationRef for ${patientRef.reference ?? "(unknown patient)"}`,
    );
  }

  const parameter = [
    { name: PATIENT_SCOPE_PARAMETER_NAME, valueReference: patientRef as Reference },
    { name: ORGANIZATION_SCOPE_PARAMETER_NAME, valueReference: organizationRef },
  ];

  return [
    {
      policy: policyRef,
      parameter,
    },
  ];
}

/**
 * ProjectMembership access for a Vibrant admin: the quarantine-including policy.
 *
 * Soft-fails (returns undefined) when the admin policy is missing — unlike the
 * standard policy, a missing admin policy means under-privilege (the admin falls
 * back to standard access), not an exposure, so it must not block role creation.
 */
export async function getAdminMembershipAccess(
  medplumAdmin: MedplumClient,
): Promise<ProjectMembershipAccess[] | undefined> {
  const policyRef = await resolveAccessPolicyRef(
    medplumAdmin,
    AccessPolicyIdentifiers.ADMIN_INCLUDES_QUARANTINED,
  );
  return policyRef ? [{ policy: policyRef }] : undefined;
}

/**
 * Upgrades a provider's AccessPolicy to the admin policy. Called when a provider
 * gains a ProviderRole at the super-org.
 *
 * Returns false if the admin policy isn't configured or no ProjectMembership is
 * found (expected cases handled gracefully). Throws only on unexpected API errors.
 */
export async function upgradeToAdminAccessPolicy(
  practitionerRef: Reference<Practitioner>,
): Promise<boolean> {
  const medplumAdmin = createMedplumAdminClient();

  const adminAccess = await getAdminMembershipAccess(medplumAdmin);
  if (!adminAccess) {
    logger.warn("Admin AccessPolicy not configured, skipping upgrade", {
      practitionerRef: practitionerRef.reference,
    });
    return false;
  }

  const memberships = await medplumAdmin.searchResources("ProjectMembership", {
    profile: practitionerRef.reference,
    _count: "1",
  });
  if (memberships.length === 0) {
    logger.warn("No ProjectMembership found for practitioner", {
      practitionerRef: practitionerRef.reference,
    });
    return false;
  }

  const membership = memberships[0] as ProjectMembership;
  await medplumAdmin.updateResource({ ...membership, access: adminAccess });

  logger.info("Upgraded provider to admin AccessPolicy", {
    practitionerRef: practitionerRef.reference,
    membershipId: membership.id,
  });
  return true;
}
